import React, { useEffect, useState } from "react";
import styles from "./PlaydateInviteBaseUnit.module.css";
import { PlaydateInvite, PlaydateInviteSuggestion, ResponseType } from "../../gql/types";
import happyFaceIcon from "../../assets/icons/purpleHappyFace.svg";
import TimeSelectionSelectionHeader from "../time/TimeSelectionSelectionHeader";
import TimeSelectionSelectionView from "../time/TimeSelectionSelectionView";
import { convertScheduleOptionsToDayPartSelections, convertScheduleOptionsToProposedDates } from "../../service/playdate/converters/converter";
import { DayPartSelection, TimeSelectionRow } from "../time/types";
import ActionButton from "../button/ActionButton";
import PlaydateInviteRecipientReviewWorkflow from "./workflow/recipient/review/PlaydateInviteRecipientReviewWorkflow";
import { useSubmitPlaydateResponse } from "../../service/playdate/requests/useSubmitResponse";
import { useToastContext } from "contexts/ToastContext";

interface PlaydateInvitationResponseCardProps {
    invite: PlaydateInvite;
    suggestion?: PlaydateInviteSuggestion;
    onDataChange: (success: boolean, invite?: PlaydateInvite) => void;
    baseZIndex?: number;
}

const PlaydateInvitationResponseCard: React.FC<PlaydateInvitationResponseCardProps> = ({
    invite,
    suggestion,
    onDataChange,
    baseZIndex,
}) => {
    const [showResponseWorkflow, setShowResponseWorkflow] = useState(false);
    const viewerResponse = invite.viewerResponse;
    const { submitResponse, loading } = useSubmitPlaydateResponse();
    const { triggerToastEvent } = useToastContext();

    const [userSelection, setUserSelection] = useState<DayPartSelection[] | TimeSelectionRow[]>([]);

    // Compute selections reactively based on viewerResponse and suggestion
    useEffect(() => {
        let newSelections: DayPartSelection[] | TimeSelectionRow[] = [];

        if (viewerResponse && viewerResponse !== null) {
            switch (viewerResponse.responseType) {
                case ResponseType.Accept:
                    newSelections = viewerResponse.selectedScheduleOptions && viewerResponse.selectedScheduleOptions !== null
                        ? convertScheduleOptionsToDayPartSelections(viewerResponse.selectedScheduleOptions)
                        : [];
                    break;
                case ResponseType.Reject:
                    newSelections = [];
                    break;
            }
        } else if (suggestion && suggestion.scheduleSuggestion) {
            newSelections = suggestion.scheduleSuggestion !== null
                ? convertScheduleOptionsToDayPartSelections(suggestion.scheduleSuggestion.scheduleOptions)
                : [];
        }
        setUserSelection(newSelections);
    }, [viewerResponse, suggestion]);

    const onUpdatedResponse = (success: boolean, invite?: PlaydateInvite) => {
        if (success) {
            onDataChange(success, invite);
            setShowResponseWorkflow(false);
        } else {
            triggerToastEvent();
        }
    };

    const handleConfirmRSVP = async () => {
        await submitResponse({
            invite,
            userSelection: userSelection,
            responseType: ResponseType.Accept,
            isEditing: !!invite.viewerResponse?.responseID,
            onCompletion: onUpdatedResponse,
        });
    };

    const headerText =
        (!viewerResponse || viewerResponse === null) && suggestion
            ? "Plii picked these times for you!"
            : "Please choose your availabilities!";

    const timeSelectionHeaderText =
        (!viewerResponse || viewerResponse === null) && suggestion
            ? "Why these options?"
            : "Proposed Dates & Times";

    return (
        <>
            <div className={styles.invitationCard}>
                <div className={styles.invitationCardContent}>
                    <div className={styles.headerContainer}>
                        <div className={styles.iconContainer}>
                            <img src={happyFaceIcon} className={styles.confirmationIcon} />
                        </div>
                        <div className={styles.headerText}>
                            {headerText}
                        </div>
                    </div>
                    <div className={styles.suggestionContainer}>
                        <TimeSelectionSelectionHeader
                            key={"timeSelectionHeader"}
                            header={timeSelectionHeaderText}
                            message={suggestion?.scheduleSuggestion &&
                                suggestion.scheduleSuggestion.reason &&
                                suggestion.scheduleSuggestion.reason !== null
                                ? suggestion.scheduleSuggestion.reason
                                : undefined}
                        />
                        <TimeSelectionSelectionView
                            mode={"daypart"}
                            proposedDates={convertScheduleOptionsToProposedDates(invite.scheduleOptions)}
                            onSelectionChange={setUserSelection}
                            initialTimes={userSelection}
                            groupByDate={true}
                        />
                    </div>
                    <div className={styles.buttonContainer}>
                        <ActionButton
                            textSize="smaller"
                            colorPattern="Transparent"
                            onClick={() => setShowResponseWorkflow(true)}
                        >
                            View Invite
                        </ActionButton>
                        <ActionButton
                            textSize="smaller"
                            colorPattern="Default"
                            hasLoadingSignal={loading}
                            onClick={handleConfirmRSVP}
                            disabled={userSelection.length === 0}
                        >
                            Confirm RSVP
                        </ActionButton>
                    </div>
                </div>
            </div >

            {showResponseWorkflow && <PlaydateInviteRecipientReviewWorkflow
                invite={invite}
                onBack={() => setShowResponseWorkflow(false)}
                onDataChange={onUpdatedResponse}
                baseZIndex={baseZIndex}
            />}
        </>
    );
};

export default PlaydateInvitationResponseCard;
